import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { addElement } from "@/store/slideEditorSlice";
import type { AudioPlayerElement, ImageElement, TextElement, VideoElement } from "@ai-teach/types";
import type { DropCenter, MediaInput, SlideHandlers } from "../types";

/**
 * Handlers for grammar slide type
 * Other slides use the canvas element system
 */
export const useFreeformHandlers = (): SlideHandlers => {
  const dispatch = useAppDispatch();
  const canvasSize = useAppSelector((state) => state.slideEditor.present.canvasSize);

  // Place the element at the drop point (centered on it) when given, else in the canvas center.
  const positionFor = (width: number, height: number, dropCenter?: DropCenter) => {
    const canvasWidth = canvasSize.height * canvasSize.aspectRatio;
    if (dropCenter) {
      return {
        x: Math.round(Math.max(0, Math.min(dropCenter.x - width / 2, canvasWidth - width))),
        y: Math.round(Math.max(0, Math.min(dropCenter.y - height / 2, canvasSize.height - height))),
      };
    }
    return {
      x: Math.round((canvasWidth - width) / 2),
      y: Math.round((canvasSize.height - height) / 2),
    };
  };

  /**
   * Add image element to canvas
   */
  const addImage = (
    { src, id, filename }: MediaInput,
    containerId?: string,
    dropCenter?: DropCenter
  ) => {
    // Handle regular canvas image drop
    if (containerId?.includes("FreeformCanvas") || !containerId) {
      // Load image to get dimensions
      const image = new Image();
      image.src = src;

      image.onload = () => {
        const { naturalWidth, naturalHeight } = image;
        const aspectRatio = naturalWidth / naturalHeight;
        const maxDimension = 300;

        let width: number;
        let height: number;

        if (naturalWidth > naturalHeight) {
          width = Math.min(naturalWidth, maxDimension);
          height = width / aspectRatio;
        } else {
          height = Math.min(naturalHeight, maxDimension);
          width = height * aspectRatio;
        }

        dispatch(
          addElement({
            type: "image",
            position: positionFor(width, height, dropCenter),
            size: { width, height },
            media: {
              src,
              id,
              filename,
            },
            border: {
              lineStyle: "none",
              stroke: "black",
              strokeWidth: 0,
              cornerRadius: 12,
            },
          } as ImageElement)
        );
      };
    }
  };

  const addAudio = ({ id, src, filename }: MediaInput, _id?: string, dropCenter?: DropCenter) => {
    dispatch(
      addElement({
        type: "audioPlayer",
        position: positionFor(70, 70, dropCenter),
        size: { width: 70, height: 70 },
        media: {
          id,
          src,
          filename,
        },
        autoPlay: true,
        loop: false,
        border: {
          lineStyle: "none",
          stroke: "transparent",
          strokeWidth: 3,
          cornerRadius: 0,
        },
      } as AudioPlayerElement)
    );
  };

  const addVideo = ({ id, src, filename }: MediaInput, _id?: string, dropCenter?: DropCenter) => {
    dispatch(
      addElement({
        type: "video",
        position: positionFor(200, 200, dropCenter),
        size: { width: 200, height: 200 },
        media: {
          id,
          src,
          filename,
        },
        // Videos autoplay by default when reached; the teacher can still turn it off.
        autoPlay: true,
        loop: false,
        border: {
          lineStyle: "none",
          stroke: "transparent",
          strokeWidth: 3,
          cornerRadius: 0,
        },
      } as VideoElement)
    );
  };

  // Other slides support image, text, and shape elements
  return {
    addImage,
    addAudio,
    addVideo,
  };
};
